import React, { useState, useEffect } from 'react';
import { 
  Wrench, 
  AlertTriangle, 
  Activity, 
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Zap,
  Thermometer,
  Gauge,
  Calendar,
  Clock,
  Settings,
  MessageCircle,
  Search,
  Filter,
  Download,
  RefreshCw,
  MapPin,
  BarChart3,
  PieChart
} from 'lucide-react';
import { useAuth0Context } from '../contexts/Auth0Context';
import { PROCOGIA_COLORS } from '../utils/themeUtils';

const EquipmentDashboard = () => {
  const { appUser } = useAuth0Context();
  const [equipment, setEquipment] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedPlant, setSelectedPlant] = useState('all');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchEquipmentData();
  }, []);

  // Export equipment data to CSV
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      // Get filtered equipment data
      const filteredData = filteredEquipment;
      
      if (filteredData.length === 0) {
        alert('No equipment data to export. Please adjust your filters.');
        return;
      }
      
      // Define CSV headers
      const headers = [
        'Equipment ID',
        'Name',
        'Type',
        'Location',
        'Status',
        'Efficiency (%)',
        'Temperature (°F)',
        'Vibration',
        'Pressure (PSI)',
        'Operating Hours',
        'Last Maintenance',
        'Next Maintenance',
        'Active Alerts'
      ];

      // Convert data to CSV format
      const csvContent = [
        headers.join(','),
        ...filteredData.map(equipment => [
          equipment.id,
          `"${equipment.name}"`,
          equipment.type,
          `"${equipment.location}"`,
          equipment.status,
          equipment.efficiency || 'N/A',
          equipment.temperature || 'N/A',
          equipment.vibration || 'N/A',
          equipment.pressure || 'N/A',
          equipment.operatingHours || 'N/A',
          equipment.lastMaintenance || 'N/A',
          equipment.nextMaintenance || 'N/A',
          `"${equipment.alerts ? equipment.alerts.join('; ') : 'None'}"`
        ].join(','))
      ].join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with current date and filter info
      const now = new Date();
      const timestamp = now.toISOString().split('T')[0];
      let filename = `equipment_report_${timestamp}`;
      if (filterStatus !== 'all') filename += `_${filterStatus}`;
      if (selectedPlant !== 'all') filename += `_${selectedPlant.replace(/\s+/g, '_')}`;
      filename += '.csv';
      
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
      console.log(`📊 Successfully exported ${filteredData.length} equipment records to ${filename}`);
      
      // Brief success feedback
      setTimeout(() => {
        alert(`✅ Successfully exported ${filteredData.length} equipment records!`);
      }, 100);
      
    } catch (error) {
      console.error('❌ Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const fetchEquipmentData = async () => {
    try {
      // Simulate comprehensive equipment data
      setStats({
        totalEquipment: 700,
        operational: 529,
        warning: 132,
        critical: 39,
        avgEfficiency: 87.3,
        avgTemperature: 78.5,
        powerConsumption: 245.7,
        maintenanceDue: 45
      });

      setEquipment([
        {
          id: 'EQ001',
          name: 'Centrifugal Pump #1',
          type: 'Pump',
          location: 'Milwaukee Plant - Zone A',
          status: 'operational',
          efficiency: 92.3,
          temperature: 75.2,
          vibration: 2.1,
          pressure: 145.8,
          lastMaintenance: '2024-05-15',
          nextMaintenance: '2024-08-15',
          operatingHours: 2847,
          alerts: []
        },
        {
          id: 'EQ045',
          name: 'Motor #45',
          type: 'Motor',
          location: 'Chicago Plant - Zone B',
          status: 'warning',
          efficiency: 84.7,
          temperature: 89.3,
          vibration: 4.2,
          pressure: null,
          lastMaintenance: '2024-04-22',
          nextMaintenance: '2024-07-22',
          operatingHours: 3521,
          alerts: ['High vibration detected', 'Temperature above normal']
        },
        {
          id: 'EQ089',
          name: 'Compressor #89',
          type: 'Compressor',
          location: 'Texas Plant - Zone C',
          status: 'critical',
          efficiency: 67.8,
          temperature: 95.7,
          vibration: 6.8,
          pressure: 98.2,
          lastMaintenance: '2024-03-10',
          nextMaintenance: '2024-06-10',
          operatingHours: 4231,
          alerts: ['Bearing failure predicted', 'Critical temperature', 'Excessive vibration']
        },
        {
          id: 'EQ127',
          name: 'Pump #127',
          type: 'Pump',
          location: 'Denver Plant - Zone A',
          status: 'operational',
          efficiency: 95.1,
          temperature: 72.4,
          vibration: 1.8,
          pressure: 152.3,
          lastMaintenance: '2024-06-01',
          nextMaintenance: '2024-09-01',
          operatingHours: 1924,
          alerts: []
        },
        {
          id: 'EQ203',
          name: 'Turbine #203',
          type: 'Turbine',
          location: 'Milwaukee Plant - Zone C',
          status: 'warning',
          efficiency: 88.9,
          temperature: 82.1,
          vibration: 3.5,
          pressure: 178.9,
          lastMaintenance: '2024-05-08',
          nextMaintenance: '2024-08-08',
          operatingHours: 2156,
          alerts: ['Scheduled maintenance due']
        }
      ]);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching equipment data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ icon, title, value, unit, change, changeType, color = "blue" }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <div className={`flex-shrink-0 p-2 rounded-lg bg-${color}-100`}>
              <div className={`text-${color}-600`}>{icon}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">{title}</div>
              <div className="text-2xl font-bold text-gray-900">
                {value}{unit && <span className="text-lg">{unit}</span>}
              </div>
            </div>
          </div>
        </div>
        {change && (
          <div className={`flex items-center text-sm ${
            changeType === 'positive' ? 'text-green-600' : 'text-red-600'
          }`}>
            {changeType === 'positive' ? 
              <TrendingUp className="w-4 h-4 mr-1" /> : 
              <TrendingDown className="w-4 h-4 mr-1" />
            }
            {change}
          </div>
        )}
      </div>
    </div>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'green';
      case 'warning': return 'yellow';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const filteredEquipment = equipment.filter(eq => {
    const matchesSearch = eq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         eq.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         eq.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || eq.status === filterStatus;
    const matchesPlant = selectedPlant === 'all' || eq.location.includes(selectedPlant);
    return matchesSearch && matchesStatus && matchesPlant;
  });

  const EfficiencyChart = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Efficiency Trends</h3>
      <div className="space-y-4">
        {[
          { time: '00:00', efficiency: 85.2, color: PROCOGIA_COLORS.blue },
          { time: '04:00', efficiency: 87.1, color: PROCOGIA_COLORS.blue },
          { time: '08:00', efficiency: 89.8, color: PROCOGIA_COLORS.green },
          { time: '12:00', efficiency: 91.2, color: PROCOGIA_COLORS.green },
          { time: '16:00', efficiency: 88.7, color: PROCOGIA_COLORS.blue },
          { time: '20:00', efficiency: 86.4, color: PROCOGIA_COLORS.blue }
        ].map((item, index) => (
          <div key={index} className="flex items-center">
            <div className="w-12 text-sm text-gray-600">{item.time}</div>
            <div className="flex-1 ml-4">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full" 
                    style={{ 
                      width: `${item.efficiency}%`,
                      backgroundColor: item.color
                    }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-12">
                  {item.efficiency}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const MaintenanceSchedule = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Maintenance Schedule</h3>
      <div className="space-y-3">
        {[
          { equipment: 'Pump #45', date: '2024-07-15', type: 'Preventive', status: 'scheduled' },
          { equipment: 'Motor #67', date: '2024-07-18', type: 'Emergency', status: 'urgent' },
          { equipment: 'Compressor #12', date: '2024-07-22', type: 'Preventive', status: 'scheduled' },
          { equipment: 'Turbine #89', date: '2024-07-25', type: 'Inspection', status: 'scheduled' }
        ].map((item, index) => (
          <div key={index} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${
                item.status === 'urgent' ? 'bg-red-500' : 'bg-blue-500'
              }`}></div>
              <div>
                <div className="text-sm font-medium text-gray-900">{item.equipment}</div>
                <div className="text-xs text-gray-500">{item.type}</div>
              </div>
            </div>
            <div className="text-sm text-gray-600">{item.date}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Equipment Dashboard</h1>
          <p className="text-gray-600">Monitor equipment performance and diagnostics</p>
        </div>
        <div className="flex items-center space-x-3">

          <button 
            onClick={exportToCSV}
            disabled={isExporting}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors text-white"
            style={{
              backgroundColor: isExporting ? PROCOGIA_COLORS.lightGrey : PROCOGIA_COLORS.blue,
              cursor: isExporting ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!isExporting) {
                e.target.style.backgroundColor = PROCOGIA_COLORS.green;
              }
            }}
            onMouseLeave={(e) => {
              if (!isExporting) {
                e.target.style.backgroundColor = PROCOGIA_COLORS.blue;
              }
            }}
          >
            {isExporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          title="Total Equipment"
          value={stats.totalEquipment}
          change="+2%"
          changeType="positive"
          color="blue"
        />
        <StatCard
          icon={<CheckCircle className="w-5 h-5" />}
          title="Operational"
          value={stats.operational}
          change="+1.5%"
          changeType="positive"
          color="green"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          title="Alerts"
          value={stats.warning + stats.critical}
          change="-3%"
          changeType="positive"
          color="red"
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5" />}
          title="Avg Efficiency"
          value={stats.avgEfficiency}
          unit="%"
          change="+2.1%"
          changeType="positive"
          color="purple"
        />
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<Thermometer className="w-5 h-5" />}
          title="Avg Temperature"
          value={stats.avgTemperature}
          unit="°F"
          change="+1.2°F"
          changeType="negative"
          color="orange"
        />
        <StatCard
          icon={<Zap className="w-5 h-5" />}
          title="Power Consumption"
          value={stats.powerConsumption}
          unit="kW"
          change="-5.3%"
          changeType="positive"
          color="yellow"
        />
        <StatCard
          icon={<Wrench className="w-5 h-5" />}
          title="Maintenance Due"
          value={stats.maintenanceDue}
          change="+8%"
          changeType="negative"
          color="indigo"
        />
        <StatCard
          icon={<Gauge className="w-5 h-5" />}
          title="System Load"
          value="73.2"
          unit="%"
          change="+2.8%"
          changeType="negative"
          color="pink"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EfficiencyChart />
        <MaintenanceSchedule />
      </div>

      {/* Equipment Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Equipment Status</h3>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search equipment..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="operational">Operational</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedPlant}
                onChange={(e) => setSelectedPlant(e.target.value)}
              >
                <option value="all">All Plants</option>
                <option value="Milwaukee">Milwaukee</option>
                <option value="Chicago">Chicago</option>
                <option value="Texas">Texas</option>
                <option value="Denver">Denver</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Efficiency</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Temperature</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Vibration</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Next Maintenance</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEquipment.map((eq) => (
                <tr key={eq.id} className="hover:bg-gray-50">
                  <td className="py-4 px-6">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{eq.name}</div>
                      <div className="text-sm text-gray-500">{eq.id} • {eq.type}</div>
                      <div className="text-xs text-gray-400 flex items-center mt-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        {eq.location}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-${getStatusColor(eq.status)}-100 text-${getStatusColor(eq.status)}-800`}>
                      {getStatusIcon(eq.status)}
                      <span className="ml-1">{eq.status}</span>
                    </span>
                    {eq.alerts.length > 0 && (
                      <div className="mt-1">
                        <span className="text-xs text-red-600">{eq.alerts.length} alert(s)</span>
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${
                            eq.efficiency > 90 ? 'bg-green-500' :
                            eq.efficiency > 80 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${eq.efficiency}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-900">{eq.efficiency}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`text-sm font-medium ${
                      eq.temperature > 85 ? 'text-red-600' :
                      eq.temperature > 80 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {eq.temperature}°F
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`text-sm font-medium ${
                      eq.vibration > 5 ? 'text-red-600' :
                      eq.vibration > 3 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {eq.vibration} mm/s
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-900">{eq.nextMaintenance}</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <button className="text-blue-600 hover:text-blue-800 p-1">
                        <Settings className="w-4 h-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-800 p-1">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );
};

export default EquipmentDashboard; 